import React, { useState, useEffect } from "react";
import {
  CloseCircleOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import { ROUTE_PATHS } from "../../routeConstants";
import { getBase64, validImageExtensions } from "../../Utils/file";
import {
  cloneSale,
  createSale,
  fetchSales,
  updateSale,
  uploadImage,
} from "../api";

const BANNER_IMAGE_HINT = {
  minWidth: 329,
  minHeight: 39,
  maxSizeKB: 1024,
  maxSizeLabel: "1 MB",
};

const SalesDashboard = () => {
  const [loader, setLoader] = useState(true);
  const [copyingLoader, setCopyingLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEndSaleModalOpen, setIsEndSaleModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToEnd, setSaleToEnd] = useState(null);
  const [uploadedBanner, setUploadedBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [form] = Form.useForm();
  const history = useHistory();

  // Fetch sales data from API
  const loadSalesData = async () => {
    try {
      setLoader(true);
      const statuses = statusFilter !== "all" ? [statusFilter] : [];
      const response = await fetchSales(
        page,
        10,
        "desc",
        statuses,
        searchQuery
      );

      if (response?.success && response?.result?.sales) {
        const transformedData = response.result.sales.map((sale) => ({
          key: sale._id,
          title: sale.title,
          status: sale.status?.toUpperCase() || "UPCOMING",
          validFrom: sale.validFrom,
          validTill: sale.validTill,
          items: sale.saleItems?.length || 0,
          createdBy: sale.createdBy?.name || "-",
          bannerUrl: sale.bannerUrl,
          rawData: sale,
        }));
        console.log({ transformedData });
        setSalesData(transformedData);
        setTotalSales(response.result.totalSales || 0);
      } else {
        setSalesData([]);
        setTotalSales(0);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      message.error("Failed to fetch sales data");
      setSalesData([]);
      setTotalSales(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadSalesData();
  }, [page, searchQuery, statusFilter]);

  const handleRefresh = () => {
    loadSalesData();
  };

  const handleCopySale = async (record) => {
    try {
      setCopyingLoader(true);

      const saleId = record.key || record.rawData?._id;
      const response = await cloneSale(saleId);

      if (response?.success) {
        message.success(`Sale "${record.title}" has been copied successfully!`);
        loadSalesData();
      } else {
        message.error(response?.message || "Failed to copy sale");
      }
    } catch (error) {
      console.error("Error copying sale:", error);
      message.error(error.message);
    } finally {
      setCopyingLoader(false);
    }
  };

  const handleMenuClick = (key, record) => {
    switch (key) {
      case "edit":
        setSelectedSale(record);
        setIsModalOpen(true);
        form.setFieldsValue({
          saleName: record.title,
          validFrom: record.validFrom ? moment(record.validFrom) : null,
          validTo: record.validTill ? moment(record.validTill) : null,
        });
        if (record.bannerUrl) {
          setUploadedBanner({
            name: record?.bannerUrl?.split("/").pop(),
            source: "Server",
            url: record.bannerUrl,
          });
          setBannerPreview(record.bannerUrl);
        }
        break;
      case "manage":
        console.log("Manage sale:", record);
        break;
      case "copy":
        handleCopySale(record);
        break;
      case "end":
        setSaleToEnd(record);
        setIsEndSaleModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleEndSale = async () => {
    try {
      const saleId = saleToEnd.key || saleToEnd.rawData?._id;
      const response = await updateSale(saleId, { status: "expired" });

      if (response?.success) {
        message.success(
          `Sale "${saleToEnd.title}" has been ended successfully!`
        );
        loadSalesData();
      } else {
        message.error(response?.message || "Failed to end sale");
      }
    } catch (error) {
      console.error("Error ending sale:", error);
      message.error(error.message);
    } finally {
      setIsEndSaleModalOpen(false);
      setSaleToEnd(null);
    }
  };

  const handleEndSaleCancel = () => {
    setIsEndSaleModalOpen(false);
    setSaleToEnd(null);
  };

  const handleNewSale = () => {
    setSelectedSale(null);
    setUploadedBanner(null);
    setIsModalOpen(true);
    form.resetFields();
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedSale(null);
    setUploadedBanner(null);
    setBannerPreview(null);
    form.resetFields();
  };

  const handleFormSubmit = async (values) => {
    try {
      // Validate banner for new sales (required for create, optional for edit)
      if (!selectedSale && !uploadedBanner) {
        message.error("Please upload a banner image");
        return;
      }

      if (!values.saleName?.trim()) {
        message.error("Please enter a sale name");
        return;
      }

      if (!values.validFrom) {
        message.error("Please select a valid from date");
        return;
      }

      if (!values.validTo) {
        message.error("Please select a valid to date");
        return;
      }

      // check if validFrom is at least 2 minutes in the future
      // Only apply this check for new sales or when editing a sale that hasn't started yet (not ACTIVE)
      if (
        !selectedSale &&
        moment(values.validFrom).isBefore(moment().add(2, "minutes"))
      ) {
        message.error(
          'The "From" time must be at least 2 minutes later than the current system time.'
        );
        return;
      }

      // Recheck that validFrom is still in the future at submit time (only for new sales)
      if (!selectedSale && moment(values.validFrom).isBefore(moment())) {
        message.error(
          "Start date and time must be in the future. Please select a valid start time."
        );
        return;
      }

      if (values.validFrom > values.validTo) {
        message.error(
          "End date and time must be after the start date and time."
        );
        return;
      }

      // Checking valid from and valid till should have atleast 5minutes gap
      if (values.validFrom && values.validTo) {
        const fromTime = moment(values.validFrom);
        const toTime = moment(values.validTo);
        const diff = toTime.diff(fromTime, "minutes");
        if (diff === 0) {
          message.error("The from date and to date cannot be the same");
          return;
        }
        if (diff < 5) {
          message.error(
            `The from date must be at least 5 minutes before the to date. Current gap is ${diff} minutes`
          );
          return;
        }
      }
      setSubmitLoader(true);

      if (selectedSale) {
        // check if sale is active and valid till date time is before 30min from current date time
        if (
          selectedSale.status === "ACTIVE" &&
          moment(values.validTo).isBefore(moment().add(30, "minutes"))
        ) {
          message.error(
            "Till Date must be at least 30 minutes later than the current date and time."
          );
          return;
        }

        // Update existing sale - only send changed fields
        const saleId = selectedSale.key || selectedSale.rawData?._id;
        const originalData = selectedSale.rawData;
        const updatePayload = {};

        // Check if title changed
        if (values.saleName !== originalData?.title) {
          updatePayload.title = values.saleName;
        }

        // Check if validFrom changed
        const newValidFrom = values.validFrom
          ?.seconds(0)
          .milliseconds(0)
          ?.toISOString();
        if (newValidFrom !== originalData?.validFrom) {
          updatePayload.validFrom = newValidFrom;
        }

        // Check if validTill changed
        const newValidTill = values.validTo
          ?.seconds(59)
          .milliseconds(999)
          ?.toISOString();
        if (newValidTill !== originalData?.validTill) {
          updatePayload.validTill = newValidTill;
        }

        // Upload new banner image only if a new file is provided
        if (uploadedBanner?.file) {
          const formData = new FormData();
          formData.append("image", uploadedBanner.file);

          const imgResponse = await uploadImage(formData);

          if (imgResponse?.code !== 0) {
            message.error("Failed to upload banner image");
            return;
          }

          updatePayload.bannerUrl = imgResponse?.result?.files?.[0];
        }

        // Check if there are any changes to update
        if (Object.keys(updatePayload).length === 0) {
          message.info("No changes detected");
          handleModalCancel();
          return;
        }

        const response = await updateSale(saleId, updatePayload);

        if (response?.success) {
          message.success("Sale updated successfully!");
          loadSalesData();
        } else {
          message.error(response?.message || "Failed to update sale");
          return;
        }
      } else {
        // Create new sale - send all fields
        let bannerUrl = "";

        if (uploadedBanner?.file) {
          const formData = new FormData();
          formData.append("image", uploadedBanner.file);

          const imgResponse = await uploadImage(formData);

          if (imgResponse?.code !== 0) {
            message.error("Failed to upload banner image");
            return;
          }

          bannerUrl = imgResponse?.result?.files?.[0];
        }

        const saleData = {
          title: values.saleName?.trim(),
          validFrom: values.validFrom
            ?.seconds(0)
            .milliseconds(0)
            ?.toISOString(),
          validTill: values.validTo
            ?.seconds(59)
            .milliseconds(999)
            ?.toISOString(),
          bannerUrl,
        };

        const response = await createSale(saleData);

        if (response?.success) {
          message.success("Sale created successfully!");
          loadSalesData();
        } else {
          message.error(response?.message || "Failed to create sale");
          return;
        }
      }

      handleModalCancel();
    } catch (error) {
      if (error?.response?.data?.type === "Conflict") {
        message.error(
          "An Active or Upcoming sale already exists for the selected period."
        );
        return;
      }
      console.error("Error submitting sale:", error);
      message.error(error.message);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleBannerUpload = (file) => {
    // Validate file type - only images allowed
    const isValidExt = validImageExtensions.indexOf(file.type) !== -1;
    if (!isValidExt) {
      message.error("Please upload a JPG, JPEG, PNG or GIF image.");
      return Upload.LIST_IGNORE;
    }

    // Validate file size
    const sizeKB = file.size / 1024;
    if (sizeKB > BANNER_IMAGE_HINT.maxSizeKB) {
      message.error(
        `Image must be smaller than ${BANNER_IMAGE_HINT.maxSizeLabel}.`
      );
      return Upload.LIST_IGNORE;
    }

    setUploadedBanner({
      file,
      name: file.name,
    });
    getBase64(file, (url) => setBannerPreview(url));
    return false;
  };

  // const handleRemoveBanner = () => {
  //   setUploadedBanner(null);
  //   setBannerPreview(null);
  // };

  const getActionMenuItems = (record) => {
    return [
      {
        key: "edit",
        label: "Edit sale",
        icon: <EditOutlined />,
        disabled: false,
        onClick: () => handleMenuClick("edit", record),
      },
      {
        key: "manage",
        label: "Manage Sale",
        icon: <SettingOutlined />,
        disabled: false,
        onClick: () =>
          history.push(
            `${ROUTE_PATHS.SALES_DASHBOARD}/${
              record.key
            }?record=${encodeURIComponent(JSON.stringify(record))}`
          ),
      },
      {
        key: "copy",
        label: "Copy Sale",
        icon: <CopyOutlined />,
        disabled: record.status !== "EXPIRED",
        onClick: () => handleMenuClick("copy", record),
      },
      {
        key: "end",
        label: "End Sale",
        icon: <CloseCircleOutlined />,
        danger: true,
        disabled: record.status !== "ACTIVE",
        onClick: () => handleMenuClick("end", record),
      },
    ];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD MMM YYYY, hh:mm a");
  };

  const displaySalesTable = () => {
    const columns = [
      {
        title: "SALES NAME",
        dataIndex: "title",
        key: "title",
        render: (text) => (
          <Typography.Text style={{ fontWeight: 500 }}>
            {text || "-"}
          </Typography.Text>
        ),
      },
      {
        title: "STATUS",
        dataIndex: "status",
        key: "status",
        render: (status, record) => {
          let color = "default";
          const statusUpper = status?.toUpperCase();
          if (statusUpper === "ACTIVE" || statusUpper === "LIVE") {
            color = "success";
          } else if (statusUpper === "EXPIRED" || statusUpper === "ENDED") {
            color = "error";
          } else if (
            statusUpper === "SCHEDULED" ||
            statusUpper === "UPCOMING"
          ) {
            color = "processing";
          } else if (statusUpper === "INACTIVE") {
            color = "default";
          }
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Tag color={color} bordered={false}>
                {status}
              </Tag>
              {record.status?.toLowerCase() === "expired" &&
                record?.rawData?.lastManuallyExpiredAt && (
                  <p
                    style={{
                      position: "relative",
                      top: 10,
                      color: "red",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {moment(record?.rawData?.lastManuallyExpiredAt).format(
                      "DD MMM YYYY, hh:mm a"
                    )}
                  </p>
                )}
            </div>
          );
        },
      },
      {
        title: "VALID",
        dataIndex: "validFrom",
        key: "valid",
        render: (text, record) => (
          <Typography.Text>
            {record.validFrom && record.validTill
              ? `${formatDate(record.validFrom)} - ${formatDate(
                  record.validTill
                )}`
              : "-"}
          </Typography.Text>
        ),
      },
      {
        title: "ITEMS",
        dataIndex: "items",
        key: "items",
        render: (items) => (
          <Typography.Text>{items || 0} Items</Typography.Text>
        ),
      },
      {
        title: "CREATED BY",
        dataIndex: "createdBy",
        key: "createdBy",
        render: (text) => <Typography.Text>{text || "-"}</Typography.Text>,
      },
      {
        title: "ACTION",
        dataIndex: "action",
        key: "action",
        render: (text, record) => (
          <Dropdown
            menu={{
              items: getActionMenuItems(record).filter(
                (item) => !item.disabled
              ),
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 20 }} />}
              onClick={(e) => e.preventDefault()}
            />
          </Dropdown>
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={salesData}
        loading={loader || copyingLoader}
        pagination={{
          current: page,
          pageSize: 10,
          total: totalSales,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} sales`,
          onChange: (newPage) => setPage(newPage),
        }}
        locale={{
          emptyText: (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 16, display: "block", marginBottom: 8 }}
              >
                No sales found
              </Typography.Text>
              <Typography.Text type="secondary">
                Create your first sale by clicking the &quot;New Sale&quot;
                button above.
              </Typography.Text>
            </div>
          ),
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  return (
    <>
      <FiltersWrapper style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <CustomSearchbar
            placeholder="Search by sales name"
            searchQuery={searchQuery}
            setSearchQuery={(value) => {
              setSearchQuery(value);
              setPage(1);
            }}
            style={{ width: 400 }}
          />
          <Tooltip title="Refresh">
            <Button size="large" onClick={handleRefresh}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          {/* <CSVLink
            filename={`sales_data_${new Date().toISOString()}.csv`}
            data={sampleSalesData}
          >
            <Button className="siteBtn grayBtn">
              <DownloadOutlined />
              Export CSV
            </Button>
          </CSVLink> */}

          <Select
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            size="large"
            style={{ width: 150 }}
          >
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="upcoming">Upcoming</Select.Option>
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="expired">Expired</Select.Option>
          </Select>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleNewSale}
            style={{
              background: "#7c3aed",
              borderColor: "#7c3aed",
            }}
          >
            New Sale
          </Button>
        </div>
      </FiltersWrapper>

      {displaySalesTable()}

      <Modal
        title={selectedSale ? "Edit Sale" : "Create New Sale"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
        destroyOnClose
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <div style={{ marginBottom: 0 }}>
            <Form.Item
              label={
                <Space>
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    SALE NAME
                  </span>
                  <Tooltip title="The Sale Name entered here will be visible to users when the sale is active.">
                    <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                  </Tooltip>
                </Space>
              }
              name="saleName"
              rules={[
                { required: true, message: "Please enter a sale name" },
                {
                  max: 150,
                  message: "Sale name cannot be more than 150 characters",
                },
              ]}
              style={{ marginBottom: 24 }}
            >
              <Input
                size="large"
                placeholder="Enter sale name"
                style={{ borderRadius: 8, fontSize: 14 }}
                maxLength={150}
                showCount
              />
            </Form.Item>
          </div>

          <Space
            size="large"
            style={{
              width: "100%",
              marginBottom: 0,
              display: "flex",
              gap: 10,
              justifyContent: "space-between",
            }}
          >
            <Form.Item
              label={
                <span style={{ fontWeight: 600, color: "#6b7280" }}>
                  VALID FROM
                  <Tooltip title='The "From" time must be at least 2 minutes later than the current system time.'>
                    <InfoCircleOutlined
                      style={{ color: "#9ca3af", marginLeft: 10 }}
                    />
                  </Tooltip>
                </span>
              }
              name="validFrom"
              rules={[
                { required: true, message: "Please select a valid from date" },
              ]}
              style={{
                width: "100%",
                marginBottom: 24,
              }}
            >
              {/* <DatePicker
                disabledDate={(current) =>
                  current && current < moment().add(0, "d").startOf("day")
                }
                showTime={{
                  use12Hours: true,
                  format: "hh:mm a",
                }}
                format="DD/MM/YYYY, hh:mm a"
                size="large"
                style={{ width: "100%", borderRadius: 8 }}
              /> */}
              <DatePicker
                disabledDate={(current) =>
                  current && current < moment().startOf("day")
                }
                showTime={{
                  use12Hours: true,
                  format: "hh:mm a",
                }}
                disabled={selectedSale?.status === "ACTIVE"}
                disabledTime={(selectedDate) => {
                  if (!selectedDate) return {};

                  const now = moment();

                  const isToday = selectedDate.isSame(now, "day");

                  if (!isToday) {
                    return {};
                  }

                  return {
                    disabledHours: () => {
                      const hours = [];

                      for (let i = 0; i < now.hour(); i += 1) {
                        hours.push(i);
                      }

                      return hours;
                    },

                    disabledMinutes: (selectedHour) => {
                      if (selectedHour === now.hour()) {
                        const minutes = [];

                        for (let i = 0; i < now.minute(); i += 1) {
                          minutes.push(i);
                        }

                        return minutes;
                      }

                      return [];
                    },
                  };
                }}
                format="DD/MM/YYYY, hh:mm a"
                size="large"
                style={{ width: "100%", borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontWeight: 600, color: "#6b7280" }}>
                  VALID TILL
                </span>
              }
              name="validTo"
              rules={[
                { required: true, message: "Please select a valid to date" },
              ]}
              style={{ width: "100%", marginBottom: 24 }}
            >
              {/* <DatePicker
                disabledDate={(current) =>
                  current && current < moment().add(0, "d").startOf("day")
                }
                showTime={{
                  use12Hours: true,
                  format: "hh:mm a",
                }}
                format="DD/MM/YYYY, hh:mm a"
                size="large"
                style={{ width: "100%", borderRadius: 8 }}
              /> */}
              <DatePicker
                disabledDate={(current) =>
                  current && current < moment().startOf("day")
                }
                showTime={{
                  use12Hours: true,
                  format: "hh:mm a",
                }}
                disabledTime={(selectedDate) => {
                  if (!selectedDate) return {};

                  const now = moment();

                  const isToday = selectedDate.isSame(now, "day");

                  if (!isToday) {
                    return {};
                  }

                  return {
                    disabledHours: () => {
                      const hours = [];

                      for (let i = 0; i < now.hour(); i += 1) {
                        hours.push(i);
                      }

                      return hours;
                    },

                    disabledMinutes: (selectedHour) => {
                      if (selectedHour === now.hour()) {
                        const minutes = [];

                        for (let i = 0; i < now.minute(); i += 1) {
                          minutes.push(i);
                        }

                        return minutes;
                      }

                      return [];
                    },
                  };
                }}
                format="DD/MM/YYYY, hh:mm a"
                size="large"
                style={{ width: "100%", borderRadius: 8 }}
              />
            </Form.Item>
          </Space>

          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontWeight: 600, color: "#6b7280" }}>
                <small style={{ color: "red" }}>*</small> UPLOAD BANNER
              </span>
            </div>
            <div>
              <Upload
                showUploadList={false}
                beforeUpload={handleBannerUpload}
                accept="image/*"
                disabled={submitLoader}
                style={{ display: "block" }}
              >
                {bannerPreview ? (
                  <div
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#111",
                    }}
                    onMouseEnter={(e) => {
                      const overlay = e.currentTarget.querySelector(
                        ".banner-hover-overlay"
                      );
                      if (overlay) overlay.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      const overlay = e.currentTarget.querySelector(
                        ".banner-hover-overlay"
                      );
                      if (overlay) overlay.style.opacity = "0";
                    }}
                  >
                    <img
                      src={bannerPreview}
                      alt="banner preview"
                      style={{
                        width: "100%",
                        display: "block",
                        maxHeight: 180,
                        objectFit: "cover",
                      }}
                    />
                    <div
                      className="banner-hover-overlay"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(17, 24, 39, 0.55)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: 0.2,
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <span>Change Image</span>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1.5px dashed #d1d5db",
                      borderRadius: 10,
                      padding: 16,
                      textAlign: "center",
                      background: "#f9fafb",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#f97316";
                      e.currentTarget.style.background = "#fff7ed";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#d1d5db";
                      e.currentTarget.style.background = "#f9fafb";
                    }}
                  >
                    <CloudUploadOutlined
                      style={{ fontSize: 24, color: "#6b7280" }}
                    />
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      Upload banner thumbnail
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        lineHeight: 1.45,
                        maxWidth: 320,
                      }}
                    >
                      Image should be horizontal, at least 2334 × 234 px. The
                      maximum image size should be 1 MB.
                      {/* Images should be horizontal, at least{" "}
                      {BANNER_IMAGE_HINT.minWidth}×{BANNER_IMAGE_HINT.minHeight}{" "}
                      px. The maximum image size should be{" "}
                      {BANNER_IMAGE_HINT.maxSizeLabel}. */}
                    </Typography.Text>
                  </div>
                )}
              </Upload>
            </div>
            <div style={{ marginTop: 8, marginLeft: 4 }}>
              <Typography.Link
                style={{ color: "#7c3aed", fontSize: 12 }}
                onClick={async () => {
                  try {
                    const response = await fetch(
                      "https://testing.tagmango.com/assets/title-0de7ee2c635b756268b057004fb5ab94.png"
                    );

                    const blob = await response.blob();

                    const url = window.URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "sample-banner.jpg";

                    document.body.appendChild(link);
                    link.click();

                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error("Download failed", error);
                  }
                }}
              >
                Click here
              </Typography.Link>
              <span style={{ fontSize: 12 }}>
                &nbsp;to download sample banner image for reference
              </span>
            </div>
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={handleModalCancel}
                disabled={submitLoader}
                style={{
                  borderRadius: 8,
                  minWidth: 120,
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitLoader}
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 8,
                  minWidth: 160,
                }}
              >
                {selectedSale ? "Save Changes" : "Create Sale"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* End Sale Confirmation Modal */}
      <Modal
        title="End Sale"
        open={isEndSaleModalOpen}
        onCancel={handleEndSaleCancel}
        centered
        destroyOnClose
        footer={[
          <Button
            key="no"
            size="large"
            onClick={handleEndSaleCancel}
            style={{ borderRadius: 8, minWidth: 100 }}
          >
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            size="large"
            danger
            onClick={handleEndSale}
            style={{ borderRadius: 8, minWidth: 100 }}
          >
            Yes
          </Button>,
        ]}
        width={500}
      >
        <div>
          <Typography.Text style={{ fontSize: 16 }}>
            Are you sure you want to end the sale &nbsp;
            <strong>&quot;{saleToEnd?.title}&quot;</strong> &nbsp;? This action
            will immediately terminate the sale and it will no longer be
            available to users.
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
};

export default SalesDashboard;
